import { z } from "zod/v4";
import { defineEndpoint, defineResource } from "@visioni/a3s-client-api";
import { stringToInt } from "@visioni/a3s-codecs";
import { FEATURE_NAME } from "../feature-name";

/**
 * Patriot clients — Patriot's accounts for the premises it monitors, as the
 * replica holds them.
 *
 * Fleet-wide only. A client does not map cleanly onto an a3s tenant, site or
 * camera (one client can cover several cameras, and client numbers are reused
 * over time), so there is no tenant-scoped view to narrow it to.
 *
 * Like the activation shapes, these are an **allowlist** of what support needs
 * to work an operational issue: who the client is, how to reach the premises,
 * what the operator is told to do, and who they call.
 *
 * **Patriot's fields keep Patriot's names**; a3s-owned fields are
 * `a3s`-prefixed, as in `activations.ts`.
 */

/** One row of the client list. */
export const ClientSummary = z.object({
  /** Patriot's client number, e.g. `10539BASE01`. */
  clientNo: z.string(),
  name: z.string(),
  /** The premises' suburb or town. */
  city: z.string().optional(),
  state: z.string().optional(),
  /** Whether Patriot has operators attend this client's alarms. */
  attendAlarm: z.boolean().optional(),
});
export type ClientSummary = z.output<typeof ClientSummary>;

export const ListFleetClientsResponse = z.object({
  items: z.array(ClientSummary),
  /** Opaque resume token; `null` when the list is exhausted. Pass back verbatim. */
  cursor: z.string().nullable(),
});
export type ListFleetClientsResponse = z.output<typeof ListFleetClientsResponse>;

/**
 * `clientNo` finds clients whose number starts with it, ignoring case — the
 * asset number support knows a client by (`11238` for `11238BASE01`). That is
 * what the replica's client index answers as a key range; searching within a
 * number or by Patriot's hand-typed name is not offered.
 *
 * Absent `clientNo`, the list is every client, in client-number order.
 */
export const ListFleetClientsQueryParams = z.object({
  limit: stringToInt.pipe(z.int().positive().max(100)).default(25),
  cursor: z.string().optional(),
  clientNo: z.string().trim().min(1).optional(),
});
export type ListFleetClientsQueryParamsWire = z.input<typeof ListFleetClientsQueryParams>;
export type ListFleetClientsQueryParams = z.output<typeof ListFleetClientsQueryParams>;

/**
 * Under the caller's tenant in the path only because every client-API route is;
 * the tenant does not narrow the list. See `fleetActivationsResource`.
 */
export const fleetClientsResource = defineResource({
  feature: FEATURE_NAME,
  resource: "patriot/fleet/clients",
  scope: "tenant",
});

export const listFleetClientsEndpoint = defineEndpoint(fleetClientsResource, {
  responseSchema: ListFleetClientsResponse,
  querySchema: ListFleetClientsQueryParams,
});

export const ClientAddress = z.object({
  addressLines: z.array(z.string()),
  city: z.string().optional(),
  state: z.string().optional(),
  country: z.string().optional(),
  postcode: z.string().optional(),
});
export type ClientAddress = z.output<typeof ClientAddress>;

/**
 * One of a client's contacts: a person or organisation Patriot's operators call
 * about the client's alarms, such as a keyholder or a patrol company.
 *
 * `callListPosition` is its place on the client's call list, from 1, and is
 * absent for a contact assigned to the client but not on the call list.
 * `type` is Patriot's user type, e.g. `Keyholder` or `Installer`. `numbers`
 * are in the order Patriot dials them.
 *
 * Personal data, shown only to support. Patriot's PINs and codes for the
 * contact are not replicated, nor its address.
 */
export const ClientContact = z.object({
  /** Patriot's id for this contact's assignment to this client. */
  clientUserId: z.number(),
  /** The contact's user number on this client. */
  userNo: z.number(),
  name: z.string().optional(),
  type: z.string().optional(),
  callListPosition: z.number().optional(),
  numbers: z.array(
    z.object({
      kind: z.enum(["mobile", "phone", "afterHours", "pager"]),
      number: z.string(),
    }),
  ),
  email: z.string().optional(),
  /** Patriot's second email for the contact, its "mobile email". */
  mobileEmail: z.string().optional(),
  /** Operator instructions for this contact at this client. */
  instructions: z.string().optional(),
});
export type ClientContact = z.output<typeof ClientContact>;

/**
 * One client in full.
 *
 * `responseInstructions` is free text written by Vision staff for the
 * monitoring operator, carried verbatim — it often holds camera IPs and site
 * access notes.
 *
 * `a3sFirstSeenAt` / `a3sLastWrittenAt` are when the replica first copied this
 * client and last copied a change to it — how fresh this view is, not anything
 * Patriot recorded.
 *
 * `externalRefNo` is Patriot's own id for the client: one Patriot record,
 * where a `clientNo` can be handed on to another site. Patriot composes the
 * `clientNo` from `clientID` (the site's panel or device id, despite its name),
 * `areaNo` and `portID`. All four are absent on a client the replica copied
 * before it held them.
 *
 * `a3sValidFrom` is present only for an earlier version asked for by
 * `version`: when the replica read the client as it is shown, which it stayed
 * until the next version.
 */
export const ClientDetail = ClientSummary.extend({
  externalRefNo: z.int().optional(),
  clientID: z.string().optional(),
  areaNo: z.string().optional(),
  portID: z.string().optional(),
  description: z.string().optional(),
  address: ClientAddress.optional(),
  responseInstructions: z.string().optional(),
  /** The client's contacts: its call list in call order, then those not on it. */
  contacts: z.array(ClientContact),
  a3sFirstSeenAt: z.string().optional(),
  a3sLastWrittenAt: z.string().optional(),
  a3sValidFrom: z.string().optional(),
});
export type ClientDetail = z.output<typeof ClientDetail>;

export const GetClientPathParams = z.object({ clientNo: z.string().min(1) });
export type GetClientPathParams = z.output<typeof GetClientPathParams>;

export const fleetClientResource = defineResource({
  feature: FEATURE_NAME,
  resource: "patriot/fleet/clients/{clientNo}",
  scope: "tenant",
  resourcePathSchema: GetClientPathParams,
});

/**
 * `version` opens the client as it was in one of its versions, named by when
 * that version began (`validFrom` from the versions list); any offset, matched
 * as the instant it names. Absent, the client as the replica holds it now.
 */
export const GetClientQueryParams = z.object({
  version: z
    .codec(z.iso.datetime({ offset: true }), z.iso.datetime(), {
      decode: (at) => new Date(at).toISOString(),
      encode: (at) => at,
    })
    .optional(),
});
export type GetClientQueryParamsWire = z.input<typeof GetClientQueryParams>;
export type GetClientQueryParams = z.output<typeof GetClientQueryParams>;

export const getFleetClientEndpoint = defineEndpoint(fleetClientResource, {
  responseSchema: ClientDetail,
  querySchema: GetClientQueryParams,
});

/**
 * One of a client's versions: the client as the replica read it at
 * `validFrom`, which held until the next version began. The replica keeps a
 * version for each change it copies, from October 2026; it reads clients once
 * a day, so a version can begin up to a day after the change in Patriot.
 */
export const ClientVersionSummary = z.object({ validFrom: z.string() });
export type ClientVersionSummary = z.output<typeof ClientVersionSummary>;

export const ListClientVersionsResponse = z.object({
  /** Newest first: the first item on the first page is the client as it is now. */
  items: z.array(ClientVersionSummary),
  /** Opaque resume token; `null` when the list is exhausted. Pass back verbatim. */
  cursor: z.string().nullable(),
});
export type ListClientVersionsResponse = z.output<typeof ListClientVersionsResponse>;

export const ListClientVersionsQueryParams = z.object({
  limit: stringToInt.pipe(z.int().positive().max(100)).default(25),
  cursor: z.string().optional(),
});
export type ListClientVersionsQueryParamsWire = z.input<typeof ListClientVersionsQueryParams>;
export type ListClientVersionsQueryParams = z.output<typeof ListClientVersionsQueryParams>;

export const fleetClientVersionsResource = defineResource({
  feature: FEATURE_NAME,
  resource: "patriot/fleet/clients/{clientNo}/versions",
  scope: "tenant",
  resourcePathSchema: GetClientPathParams,
});

export const listFleetClientVersionsEndpoint = defineEndpoint(fleetClientVersionsResource, {
  responseSchema: ListClientVersionsResponse,
  querySchema: ListClientVersionsQueryParams,
});
